import React from 'react';
import Skeleton from './Skeleton';

/**
 * DashboardSkeleton
 * Shimmer loading placeholder that mirrors the manager Dashboard layout:
 *   - page header
 *   - 3 stat cards (full width)
 *   - split: main list card (left) + rail of 3 cards (right)
 *
 * Built from the base <Skeleton /> block (pure Tailwind shimmer). Add future
 * page skeletons alongside this file.
 */

// Shared card shell — identical to the real Dashboard cards.
const CARD =
  'bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]';

// Alias so the markup below reads compactly.
const S = Skeleton;

const StatCardSkeleton = () => (
  <div className={`${CARD} px-6 py-4 flex flex-col gap-3`}>
    <div className="flex items-center gap-3">
      <S className="w-10 h-10 rounded-xl" />
      <S className="h-3.5 w-24 rounded-md" />
    </div>
    <div className="flex items-end gap-3">
      <S className="h-10 w-16 rounded-xl" />
      <S className="h-5 w-20 rounded-lg mb-1.5" />
    </div>
  </div>
);

const VisitRowSkeleton = () => (
  <div className="flex items-center gap-[15px] px-[22px] py-[13px] border-t border-[#EEF2F8] dark:border-gray-800">
    <S className="h-3.5 w-[56px] rounded-md shrink-0" />
    <div className="flex items-center gap-[11px] w-[188px] shrink-0">
      <S className="w-[34px] h-[34px] rounded-xl shrink-0" />
      <div className="flex-1 space-y-1.5">
        <S className="h-3 w-28 rounded-md" />
        <S className="h-2.5 w-16 rounded-md" />
      </div>
    </div>
    <S className="h-3 flex-1 rounded-md" />
    <S className="h-6 w-16 rounded-lg shrink-0" />
  </div>
);

const RailCardSkeleton = ({ rows = 2 }) => (
  <div className={CARD}>
    <div className="flex items-center justify-between px-[22px] pt-[18px] pb-3">
      <div className="flex items-center gap-[11px]">
        <S className="w-1 h-[18px] rounded" />
        <S className="h-4 w-28 rounded-md" />
      </div>
      <S className="h-3.5 w-14 rounded-md" />
    </div>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 px-[22px] py-3 border-t border-[#EEF2F8] dark:border-gray-800">
        <S className="w-11 h-11 rounded-xl shrink-0" />
        <div className="flex-1 space-y-2">
          <S className="h-3 w-3/4 rounded-md" />
          <S className="h-2.5 w-1/2 rounded-md" />
        </div>
      </div>
    ))}
  </div>
);

const DashboardSkeleton = () => (
  <div className="w-full pb-10 flex flex-col gap-5 -mt-2 md:-mt-4">
    {/* Page header */}
    <div>
      <S className="h-7 w-44 rounded-xl" />
      <S className="h-3.5 w-72 rounded-md mt-2" />
    </div>

    {/* Stat row */}
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-[22px]">
      <StatCardSkeleton />
      <StatCardSkeleton />
      <StatCardSkeleton />
      <StatCardSkeleton />
    </div>

    {/* Split: main list + rail */}
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-[22px] items-start">
      {/* Main list */}
      <div className={CARD}>
        <div className="flex items-center justify-between px-[22px] pt-[18px] pb-3">
          <div className="flex items-center gap-[11px]">
            <S className="w-1 h-[18px] rounded" />
            <S className="h-4 w-32 rounded-md" />
          </div>
          <S className="h-3.5 w-28 rounded-md" />
        </div>
        {Array.from({ length: 6 }).map((_, i) => <VisitRowSkeleton key={i} />)}
      </div>

      {/* Rail */}
      <div className="flex flex-col gap-[22px]">
        <RailCardSkeleton rows={2} />
        <RailCardSkeleton rows={2} />
        <RailCardSkeleton rows={2} />
      </div>
    </div>
  </div>
);

export default DashboardSkeleton;
